import React from 'react';
import { ResponsiveLine } from '@nivo/line';
import { RATING_BANDS } from '../shared/sentimentBands';
import { sectorMovePct } from '../shared/sectorRotation';
import {
  HIGHLIGHT_COLORS, MAX_HIGHLIGHTS, RECESSIVE_STROKE, GRID_INK, AXIS_INK,
  shortDate, fmtPct, useSectorHistory, usePinnedSlots, Pill, SectorPinPills, LoadingNote
} from './sectorChartShared';

/**
 * Sector time series (issue #278): each sector's Fear & Greed score, or its
 * cumulative cap-weighted price move, over a chosen window.
 *
 * Eleven sectors is too many for a categorical palette (the validated set
 * holds three all-pairs-safe hues), so the chart is highlight-driven: every
 * sector is drawn as a thin recessive line, the sector average as an ink
 * line, and the user pins up to MAX_HIGHLIGHTS sectors which take a fixed
 * colour slot for as long as they stay pinned -- colour follows the sector,
 * never its rank, and unpinning one never repaints the others.
 *
 * Layout is inline-styled like the rest of the sector panel: the prebuilt
 * `tailwind-output.css` is missing utilities and a missing class fails
 * silently (see CLAUDE.md).
 */

const WINDOWS = [
  { days: 30, label: '1M' },
  { days: 90, label: '3M' },
  { days: 180, label: '6M' }
];

const METRICS = {
  sentiment: { label: 'Sentiment', title: 'Sector sentiment over time' },
  performance: { label: 'Price move', title: 'Sector price moves over time' }
};

const AVERAGE_STROKE = '#0b0b0b';
const AVERAGE_ID = 'Sector average';

const BAND_EDGES = RATING_BANDS.map((b) => b.maxExclusive).filter(Number.isFinite);

const fmtSentiment = (v) => (v == null ? '—' : v.toFixed(1));

/**
 * Sentiment: one point per stored score. Performance: the stored per-session
 * moves compounded from the first session in the window, so every sector
 * starts at 0 and the lines are comparable. Both return
 * `{ dates, bySector: Map<sector, Map<date, value>>, average: Map<date, value>, detail }`.
 */
function buildSeries(data, metric) {
  if (metric === 'sentiment') {
    const bySector = new Map();
    const dates = new Set();
    for (const row of data.sentiment) {
      dates.add(row.date);
      if (!bySector.has(row.sector)) bySector.set(row.sector, new Map());
      bySector.get(row.sector).set(row.date, row.score);
    }
    const average = new Map(data.sentiment_average.map((r) => [r.date, r.score]));
    return { dates: [...dates].sort(), bySector, average, detail: new Map() };
  }

  const bySector = new Map();
  const cumulative = new Map();
  const detail = new Map();
  const dates = [...new Set(data.performance.map((r) => r.date))].sort();
  for (const date of dates) {
    const rows = data.performance.filter((r) => r.date === date);
    for (const row of rows) {
      const move = sectorMovePct(row);
      if (move == null) continue;
      const prior = cumulative.get(row.sector) ?? 0;
      const next = ((1 + prior / 100) * (1 + move / 100) - 1) * 100;
      cumulative.set(row.sector, next);
      if (!bySector.has(row.sector)) bySector.set(row.sector, new Map());
      bySector.get(row.sector).set(date, next);
      detail.set(`${row.sector}|${date}`, row);
    }
  }
  // Equal-weighted across whichever sectors have a value that day.
  const average = new Map();
  for (const date of dates) {
    const values = [...bySector.values()].map((m) => m.get(date)).filter((v) => v != null);
    if (values.length) average.set(date, values.reduce((s, v) => s + v, 0) / values.length);
  }
  return { dates, bySector, average, detail };
}

const toPoints = (dates, values) => dates.map((date) => ({ x: date, y: values.get(date) ?? null }));

const LABEL_GAP = 13;

/**
 * Direct end labels for highlighted series and the average: the relief for a
 * sub-3:1 hue. Labels are pushed apart top-down so two series ending at the
 * same value never overprint.
 */
const EndLabels = ({ series }) => {
  const labels = series
    .filter((s) => s.color !== RECESSIVE_STROKE)
    .map((s) => {
      const last = [...s.data].reverse().find((p) => p.data.y != null);
      return last && { id: s.id, color: s.color, x: last.position.x + 6, y: last.position.y + 4 };
    })
    .filter(Boolean)
    .sort((a, b) => a.y - b.y);
  for (let i = 1; i < labels.length; i += 1) {
    labels[i].y = Math.max(labels[i].y, labels[i - 1].y + LABEL_GAP);
  }
  return (
    <g>
      {labels.map((l) => (
        <text key={l.id} x={l.x} y={l.y} fontSize={11} fontWeight={600} fill={l.color}>
          {l.id === AVERAGE_ID ? 'Avg' : l.id}
        </text>
      ))}
    </g>
  );
};

/** Zero baseline for the price-move view (Nivo's `markers` prop wants legend props it does not need). */
const ZeroLine = ({ yScale, innerWidth }) => (
  <line x1={0} x2={innerWidth} y1={yScale(0)} y2={yScale(0)} stroke={RECESSIVE_STROKE} strokeWidth={1} />
);

const SliceTooltip = ({ slice, metric, colorFor, detail }) => {
  const date = slice.points[0]?.data.x;
  const fmt = metric === 'sentiment' ? fmtSentiment : fmtPct;
  const rows = [...slice.points]
    .filter((p) => p.data.y != null)
    .sort((a, b) => (a.serieId === AVERAGE_ID ? -1 : b.serieId === AVERAGE_ID ? 1 : b.data.y - a.data.y));
  return (
    <div
      style={{
        background: '#fff',
        border: '1px solid #e5e7eb',
        borderRadius: '6px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
        padding: '8px 10px',
        fontSize: '0.7rem',
        minWidth: '190px'
      }}
    >
      <div style={{ fontWeight: 600, color: '#111827', marginBottom: '4px' }}>{date && shortDate(date)}</div>
      {rows.map((p) => {
        const color = colorFor(p.serieId);
        const pinned = color !== RECESSIVE_STROKE;
        const day = metric === 'performance' ? detail.get(`${p.serieId}|${date}`) : null;
        return (
          <div
            key={p.serieId}
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              gap: '12px',
              color: pinned ? '#111827' : '#6b7280',
              fontWeight: pinned ? 600 : 400,
              lineHeight: 1.7
            }}
          >
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: '5px' }}>
              <span style={{ width: 8, height: 8, borderRadius: '50%', background: color, display: 'inline-block' }} />
              {p.serieId}
            </span>
            <span style={{ fontVariantNumeric: 'tabular-nums' }}>
              {fmt(p.data.y)}
              {day && (
                <span style={{ color: '#9ca3af', fontWeight: 400 }}>
                  {' '}
                  ({fmtPct(sectorMovePct(day))} day, {day.advancers}↑ {day.decliners}↓)
                </span>
              )}
            </span>
          </div>
        );
      })}
    </div>
  );
};

/**
 * `metric` locks the chart to one series and hides the metric toggle: the Fear
 * & Greed section mounts it as `sentiment`, the market heatmap's "Over time"
 * tab as `performance`. Omitted, the user can switch between the two.
 * `embedded`: the host already titles the panel, so no top margin.
 */
const SectorHistoryChart = ({ metric: fixedMetric, embedded = false, defaultPinned = [] }) => {
  const [chosenMetric, setMetric] = React.useState('sentiment');
  const metric = fixedMetric || chosenMetric;
  const [days, setDays] = React.useState(WINDOWS[1].days);
  const pins = usePinnedSlots(defaultPinned);
  const { slots, slotOf } = pins;

  const { data, isLoading, isError } = useSectorHistory(days);

  const series = React.useMemo(() => (data ? buildSeries(data, metric) : null), [data, metric]);

  const colorFor = (id) => {
    if (id === AVERAGE_ID) return AVERAGE_STROKE;
    const slot = slotOf(id);
    return slot === -1 ? RECESSIVE_STROKE : HIGHLIGHT_COLORS[slot];
  };

  if (isLoading) {
    return <LoadingNote>Loading sector history…</LoadingNote>;
  }
  if (isError || !data) return null;

  const sectors = data.sectors || [];
  const hasPoints = series.dates.length > 1;

  // Recessive lines first, pinned next, average last so it draws on top.
  const chartData = [
    ...sectors.filter((s) => slotOf(s) === -1),
    ...slots.filter(Boolean)
  ]
    .filter((s) => series.bySector.has(s))
    .map((s) => ({ id: s, color: colorFor(s), data: toPoints(series.dates, series.bySector.get(s)) }))
    .concat(series.average.size ? [{ id: AVERAGE_ID, color: AVERAGE_STROKE, data: toPoints(series.dates, series.average) }] : []);

  const isSentiment = metric === 'sentiment';
  const tickEvery = Math.max(1, Math.ceil(series.dates.length / 8));
  const xTicks = series.dates.filter((_, i) => i % tickEvery === 0 || i === series.dates.length - 1);
  const first = series.dates[0];

  return (
    <div style={{ marginTop: embedded ? 0 : '18px' }}>
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', flexWrap: 'wrap', gap: '8px' }}>
        <h4 style={{ fontSize: '0.85rem', fontWeight: 600, color: '#111827', margin: 0 }}>
          {fixedMetric ? METRICS[fixedMetric].title : 'Sectors over time'}
        </h4>
        <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
          {!fixedMetric && Object.entries(METRICS).map(([key, m]) => (
            <Pill key={key} active={metric === key} onClick={() => setMetric(key)}>{m.label}</Pill>
          ))}
          {!fixedMetric && <span style={{ width: '6px' }} />}
          {WINDOWS.map((w) => (
            <Pill key={w.days} active={days === w.days} onClick={() => setDays(w.days)}>{w.label}</Pill>
          ))}
        </div>
      </div>

      <SectorPinPills sectors={sectors} pins={pins} />

      {!hasPoints ? (
        <div style={{ fontSize: '0.72rem', color: '#6b7280', padding: '14px 0' }}>
          {series.dates.length === 1
            ? `One daily snapshot so far (${shortDate(first)}); the line appears once a second day is recorded.`
            : 'No daily snapshots stored for this window yet.'}
        </div>
      ) : (
        <div className="sector-history-thin" style={{ height: 280 }}>
          <ResponsiveLine
            data={chartData}
            margin={{ top: 12, right: 148, bottom: 32, left: 40 }}
            xScale={{ type: 'point' }}
            yScale={isSentiment ? { type: 'linear', min: 0, max: 100 } : { type: 'linear', min: 'auto', max: 'auto' }}
            curve="monotoneX"
            colors={(d) => d.color}
            lineWidth={2}
            layers={['grid', ...(isSentiment ? [] : [ZeroLine]), 'axes', 'crosshair', 'lines', 'slices', 'mesh', EndLabels]}
            enableSlices="x"
            sliceTooltip={({ slice }) => <SliceTooltip slice={slice} metric={metric} colorFor={colorFor} detail={series.detail} />}
            enableGridX={false}
            gridYValues={isSentiment ? BAND_EDGES : undefined}
            axisLeft={{
              tickSize: 0,
              tickPadding: 6,
              tickValues: isSentiment ? [0, 25, 50, 75, 100] : 5,
              format: isSentiment ? undefined : (v) => `${v > 0 ? '+' : ''}${v}%`
            }}
            axisBottom={{ tickSize: 0, tickPadding: 8, tickValues: xTicks, format: shortDate }}
            theme={{
              text: { fontSize: 11, fill: AXIS_INK },
              grid: { line: { stroke: GRID_INK, strokeWidth: 1 } },
              axis: { domain: { line: { stroke: 'transparent' } } },
              crosshair: { line: { stroke: '#9ca3af', strokeWidth: 1, strokeOpacity: 1 } }
            }}
            isInteractive
            useMesh={false}
            animate={false}
            enablePoints={false}
            legends={[]}
          />
        </div>
      )}

      {/* Recessive lines are thinner than pinned ones; Nivo has one lineWidth, so the CSS does it. */}
      <style>{`.sector-history-thin path[stroke="${RECESSIVE_STROKE}"] { stroke-width: 1px; }`}</style>

      <div style={{ fontSize: '0.65rem', color: '#9ca3af', marginTop: '6px', lineHeight: 1.5 }}>
        {isSentiment
          ? `Daily Fear & Greed score per sector (0–100, same bands as the gauge), recorded since ${shortDate(first)}. Hover for every sector's value; pin up to ${MAX_HIGHLIGHTS}.`
          : `Cap-weighted daily move across each sector's liquid constituents, compounded from ${shortDate(first)} (settled closes, each stock clipped at ±25%). The average is equal-weighted across sectors.`}
      </div>
    </div>
  );
};

export default SectorHistoryChart;
